/**
 * Turns per-frame MediaPipe detections into identity-stable, smoothed `TrackedHand`s.
 *
 * MediaPipe emits an unordered list each frame with no persistent identity, and its
 * handedness label is unreliable on mirrored previews. Both would make the instrument swap
 * pitch and volume whenever the hands cross or one blinks out. So this module:
 *
 *  1. converts landmarks into screen space through the canonical coordinate pipeline,
 *  2. matches detections to existing tracks by predicted position, with handedness and hand
 *     size as secondary cues that only decide near-ties,
 *  3. smooths the control points with a One Euro filter, and every landmark with a softer
 *     one (the skeleton can afford lag; the note cannot),
 *  4. coasts a track through short dropouts before retiring it.
 *
 * At most two hands are tracked, so the assignment problem is solved exactly by comparing
 * the two possible pairings rather than running a general Hungarian solver.
 */

import type { VideoCoordinateMapper } from '../camera/videoCoordinates';
import { OneEuroFilter, Point2Smoother, Vector3Smoother, softenConfig } from './LandmarkSmoother';
import type { OneEuroConfig } from './LandmarkSmoother';
import { DEFAULT_ONE_EURO } from './LandmarkSmoother';
import type { HandLandmarkSample, Handedness, Point2, TrackedHand, Vector3 } from './types';
import {
  HAND_LANDMARK_COUNT,
  LANDMARK,
  PALM_LANDMARKS,
  createVector3,
  handOpenness,
  handSpan,
} from './types';

export interface HandIdentityOptions {
  /**
   * Maximum distance, in screen units, between a prediction and a detection for them to be
   * considered the same hand. 0.45 is a little under half a screen height: generous enough
   * for a fast swipe at 20 Hz inference, tight enough that two hands never steal each other.
   */
  maxMatchDistance: number;
  /** How long an unmatched track is coasted before it is deleted, in ms. */
  trackTimeoutMs: number;
  /** Detections below this handedness score are ignored entirely. */
  minConfidence: number;
  smoothing: Partial<OneEuroConfig>;
}

export const DEFAULT_HAND_IDENTITY_OPTIONS: HandIdentityOptions = {
  maxMatchDistance: 0.45,
  trackTimeoutMs: 700,
  minConfidence: 0.4,
  smoothing: {},
};

/**
 * Weight of the two secondary matching cues, in screen units.
 *
 * Both are deliberately small compared with the distances hands normally keep from each
 * other: they exist to break a near-tie during a crossing or an occlusion, where position
 * alone is a coin flip, and must never outvote a clear positional match.
 */
const HANDEDNESS_PENALTY = 0.08;
const SIZE_PENALTY = 0.05;

/** How quickly the per-track handedness vote follows the model's per-frame label. */
const HANDEDNESS_VOTE_ALPHA = 0.25;

/** Openness drives timbre, so it is filtered harder than position and independently of it. */
const OPENNESS_FILTER: OneEuroConfig = { minCutoff: 1.1, beta: 0.6, dCutoff: 1.0 };

/** Handedness as a signed vote: -1 is firmly Left, +1 is firmly Right, 0 is undecided. */
function handednessValue(handedness: Handedness): number {
  if (handedness === 'Right') return 1;
  if (handedness === 'Left') return -1;
  return 0;
}

/** One detection for the current frame, already converted to screen space. */
interface Observation {
  /** All 21 landmarks in screen space. */
  points: Point2[];
  palm: Vector3;
  indexTip: Vector3;
  thumbTip: Vector3;
  middleTip: Vector3;
  wrist: Vector3;
  handedness: Handedness;
  confidence: number;
  span: number;
  /** Index into the source sample array. */
  sourceIndex: number;
}

class Track {
  readonly id: string;

  handedness: Handedness = 'Unknown';

  confidence = 0;

  lastSeen = 0;

  timestamp = 0;

  visible = false;

  span = 0;

  /** Running -1..+1 vote over the model's handedness labels; see `handednessValue`. */
  handednessVote = 0;

  readonly palm = new Vector3Smoother();

  readonly indexTip = new Vector3Smoother();

  readonly thumbTip = new Vector3Smoother();

  readonly middleTip = new Vector3Smoother();

  readonly wrist = new Vector3Smoother();

  /** One filter per landmark, for the skeleton overlay and the openness reading. */
  readonly points: Point2Smoother[] = [];

  private readonly openness = new OneEuroFilter(OPENNESS_FILTER);

  /** Reused output object so the render loop never allocates. */
  readonly output: TrackedHand;

  /** Index of the detection this track consumed on the current frame, -1 when unmatched. */
  sourceIndex = -1;

  /**
   * Last *raw* observed palm position. Matching predicts from this rather than from the
   * smoothed estimate: the filter deliberately lags, and during a hand crossing that lag is
   * enough for the nearest-neighbour test to pick the wrong detection.
   */
  private rawX = 0;

  private rawY = 0;

  constructor(id: string) {
    this.id = id;
    for (let i = 0; i < HAND_LANDMARK_COUNT; i += 1) this.points.push(new Point2Smoother());
    this.output = {
      id,
      handedness: 'Unknown',
      confidence: 0,
      palm: createVector3(),
      indexTip: createVector3(),
      thumbTip: createVector3(),
      middleTip: createVector3(),
      wrist: createVector3(),
      velocity: createVector3(),
      // Direct references to the filters' output objects: they are mutated in place, so the
      // tracked hand always exposes the current smoothed skeleton without any copying.
      landmarks: this.points.map((point) => point.value),
      openness: 0,
      span: 0,
      visible: false,
      timestamp: 0,
    };
  }

  setSmoothing(config: Partial<OneEuroConfig>): void {
    this.palm.setConfig(config);
    this.indexTip.setConfig(config);
    this.thumbTip.setConfig(config);
    this.middleTip.setConfig(config);
    this.wrist.setConfig(config);
    const visual = softenConfig({ ...DEFAULT_ONE_EURO, ...config });
    for (const point of this.points) point.setConfig(visual);
  }

  /** Where we expect this hand to be at `timeMs`, given its last measurement and velocity. */
  predict(timeMs: number, out: Point2): Point2 {
    const dt = Math.min(0.25, Math.max(0, (timeMs - this.timestamp) / 1000));
    out.x = this.rawX + this.palm.velocity.x * dt;
    out.y = this.rawY + this.palm.velocity.y * dt;
    return out;
  }

  observe(observation: Observation, timeMs: number): void {
    const t = timeMs / 1000;
    const fresh = !this.palm.isInitialized;
    this.updatePoint(this.palm, observation.palm, t, fresh);
    this.updatePoint(this.indexTip, observation.indexTip, t, fresh);
    this.updatePoint(this.thumbTip, observation.thumbTip, t, fresh);
    this.updatePoint(this.middleTip, observation.middleTip, t, fresh);
    this.updatePoint(this.wrist, observation.wrist, t, fresh);

    for (let i = 0; i < this.points.length; i += 1) {
      const filter = this.points[i];
      const source = observation.points[i];
      if (!filter || !source) continue;
      if (fresh) filter.snapTo(source.x, source.y, t);
      else filter.update(source.x, source.y, t);
    }

    const openAmount = handOpenness(this.output.landmarks);
    if (fresh) this.openness.snapTo(openAmount, t);
    else this.openness.filter(openAmount, t);

    this.span = fresh ? observation.span : this.span + (observation.span - this.span) * 0.2;
    if (observation.handedness !== 'Unknown') {
      const target = handednessValue(observation.handedness);
      this.handednessVote += (target - this.handednessVote) * HANDEDNESS_VOTE_ALPHA;
    }

    this.rawX = observation.palm.x;
    this.rawY = observation.palm.y;
    this.handedness = observation.handedness;
    this.confidence = observation.confidence;
    this.lastSeen = timeMs;
    this.timestamp = timeMs;
    this.visible = true;
    this.sourceIndex = observation.sourceIndex;
  }

  private updatePoint(target: Vector3Smoother, source: Vector3, t: number, fresh: boolean): void {
    if (fresh) target.snapTo(source.x, source.y, source.z, t);
    else target.update(source.x, source.y, source.z, t);
  }

  markMissing(timeMs: number): void {
    this.visible = false;
    this.timestamp = timeMs;
    this.sourceIndex = -1;
  }

  syncOutput(): TrackedHand {
    const o = this.output;
    o.handedness = this.handedness;
    o.confidence = this.confidence;
    o.visible = this.visible;
    o.timestamp = this.timestamp;
    o.openness = clamp01(this.openness.current);
    o.span = this.span;
    copyInto(o.palm, this.palm.value);
    copyInto(o.indexTip, this.indexTip.value);
    copyInto(o.thumbTip, this.thumbTip.value);
    copyInto(o.middleTip, this.middleTip.value);
    copyInto(o.wrist, this.wrist.value);
    copyInto(o.velocity, this.palm.velocity);
    return o;
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function copyInto(target: Vector3, source: { x: number; y: number; z: number }): void {
  target.x = source.x;
  target.y = source.y;
  target.z = source.z;
}

/**
 * Cost of pairing a track's handedness history with one detection's label.
 *
 * The vote, not the last frame's label, is what counts: MediaPipe flips the label on a
 * mirrored preview often enough that a single frame is worthless, while the average over a
 * second of frames is usually right.
 */
function handednessPenalty(vote: number, observed: Handedness): number {
  const observedValue = handednessValue(observed);
  if (observedValue === 0 || vote === 0) return 0;
  const agreement = vote * observedValue;
  return agreement >= 0 ? 0 : Math.min(1, -agreement) * HANDEDNESS_PENALTY;
}

/** Cost of pairing two hands of different apparent size, normalised by the larger one. */
function sizePenalty(trackSpan: number, observedSpan: number): number {
  const larger = Math.max(trackSpan, observedSpan);
  if (larger < 1e-4) return 0;
  return (Math.abs(trackSpan - observedSpan) / larger) * SIZE_PENALTY;
}

/**
 * Copy one landmark into a control point of `observation`.
 *
 * The x/y come from the already-converted screen points so the conversion runs once per
 * landmark per frame; only z has to be read from the raw sample.
 */
function readLandmark(
  sample: HandLandmarkSample,
  index: number,
  mapper: VideoCoordinateMapper,
  observation: Observation,
  out: Vector3,
): void {
  const point = observation.points[index];
  const landmark = sample.landmarks[index];
  if (!point || !landmark) return;
  out.x = point.x;
  out.y = point.y;
  out.z = mapper.toScreenDepth(landmark.z);
}

export class HandIdentityTracker {
  private options: HandIdentityOptions;

  private readonly tracks: Track[] = [];

  private nextId = 1;

  /** Reused buffers. */
  private readonly observations: Observation[] = [];

  private readonly observationPool: Observation[] = [];

  private readonly resultBuffer: TrackedHand[] = [];

  private readonly predictionA: Point2 = { x: 0, y: 0 };

  private readonly predictionB: Point2 = { x: 0, y: 0 };

  constructor(options: Partial<HandIdentityOptions> = {}) {
    this.options = { ...DEFAULT_HAND_IDENTITY_OPTIONS, ...options };
  }

  setSmoothing(config: Partial<OneEuroConfig>): void {
    this.options = { ...this.options, smoothing: config };
    for (const track of this.tracks) track.setSmoothing(config);
  }

  setOptions(options: Partial<HandIdentityOptions>): void {
    this.options = { ...this.options, ...options };
  }

  reset(): void {
    this.tracks.length = 0;
    this.resultBuffer.length = 0;
  }

  /**
   * Ingest one inference result.
   *
   * @param samples   raw detections for this frame (may be empty)
   * @param mapper    live coordinate mapper (mirroring/crop/rotation aware)
   * @param timeMs    `performance.now()` timestamp of the frame
   * @returns         reused array of tracked hands
   */
  update(
    samples: readonly HandLandmarkSample[],
    mapper: VideoCoordinateMapper,
    timeMs: number,
  ): readonly TrackedHand[] {
    this.buildObservations(samples, mapper);
    this.assign(timeMs);
    this.retire(timeMs);

    this.resultBuffer.length = 0;
    for (const track of this.tracks) this.resultBuffer.push(track.syncOutput());
    return this.resultBuffer;
  }

  private buildObservations(
    samples: readonly HandLandmarkSample[],
    mapper: VideoCoordinateMapper,
  ): void {
    this.observations.length = 0;
    const limit = Math.min(samples.length, 2);
    for (let i = 0; i < limit; i += 1) {
      const sample = samples[i];
      if (!sample || sample.landmarks.length < HAND_LANDMARK_COUNT) continue;
      if (sample.score < this.options.minConfidence) continue;

      const observation = this.observationAt(i);
      for (let j = 0; j < HAND_LANDMARK_COUNT; j += 1) {
        const landmark = sample.landmarks[j];
        const target = observation.points[j];
        if (!landmark || !target) continue;
        mapper.toScreen(landmark.x, landmark.y, target);
      }

      readLandmark(sample, LANDMARK.INDEX_TIP, mapper, observation, observation.indexTip);
      readLandmark(sample, LANDMARK.THUMB_TIP, mapper, observation, observation.thumbTip);
      readLandmark(sample, LANDMARK.MIDDLE_TIP, mapper, observation, observation.middleTip);
      readLandmark(sample, LANDMARK.WRIST, mapper, observation, observation.wrist);
      this.readPalm(sample, mapper, observation);

      observation.handedness = sample.handedness;
      observation.confidence = sample.score;
      observation.span = handSpan(observation.points);
      observation.sourceIndex = i;
      this.observations.push(observation);
    }
  }

  private readPalm(
    sample: HandLandmarkSample,
    mapper: VideoCoordinateMapper,
    observation: Observation,
  ): void {
    let sx = 0;
    let sy = 0;
    let sz = 0;
    let count = 0;
    for (const index of PALM_LANDMARKS) {
      const point = observation.points[index];
      const landmark = sample.landmarks[index];
      if (!point || !landmark) continue;
      sx += point.x;
      sy += point.y;
      sz += landmark.z;
      count += 1;
    }
    if (count === 0) return;
    observation.palm.x = sx / count;
    observation.palm.y = sy / count;
    observation.palm.z = mapper.toScreenDepth(sz / count);
  }

  private observationAt(index: number): Observation {
    let observation = this.observationPool[index];
    if (!observation) {
      const points: Point2[] = [];
      for (let i = 0; i < HAND_LANDMARK_COUNT; i += 1) points.push({ x: 0, y: 0 });
      observation = {
        points,
        palm: createVector3(),
        indexTip: createVector3(),
        thumbTip: createVector3(),
        middleTip: createVector3(),
        wrist: createVector3(),
        handedness: 'Unknown',
        confidence: 0,
        span: 0,
        sourceIndex: index,
      };
      this.observationPool[index] = observation;
    }
    return observation;
  }

  /** Cost of assigning `observation` to `track`, or Infinity when it exceeds the gate. */
  private cost(track: Track, observation: Observation, timeMs: number, out: Point2): number {
    track.predict(timeMs, out);
    const distance = Math.hypot(out.x - observation.palm.x, out.y - observation.palm.y);
    if (distance > this.options.maxMatchDistance) return Number.POSITIVE_INFINITY;
    return (
      distance +
      handednessPenalty(track.handednessVote, observation.handedness) +
      sizePenalty(track.span, observation.span)
    );
  }

  private assign(timeMs: number): void {
    const observations = this.observations;
    const tracks = this.tracks;

    if (tracks.length === 0) {
      for (const observation of observations) this.spawn(observation, timeMs);
      return;
    }

    const matchedTracks = new Set<Track>();
    const matchedObservations = new Set<Observation>();

    if (tracks.length === 2 && observations.length === 2) {
      // Exact 2x2 assignment: compare the only two pairings.
      const [t0, t1] = tracks as [Track, Track];
      const [o0, o1] = observations as [Observation, Observation];
      const straight =
        this.cost(t0, o0, timeMs, this.predictionA) + this.cost(t1, o1, timeMs, this.predictionB);
      const crossed =
        this.cost(t0, o1, timeMs, this.predictionA) + this.cost(t1, o0, timeMs, this.predictionB);
      const pairs: [Track, Observation][] =
        straight <= crossed
          ? [
              [t0, o0],
              [t1, o1],
            ]
          : [
              [t0, o1],
              [t1, o0],
            ];
      for (const [track, observation] of pairs) {
        if (this.cost(track, observation, timeMs, this.predictionA) === Number.POSITIVE_INFINITY) {
          continue;
        }
        track.observe(observation, timeMs);
        matchedTracks.add(track);
        matchedObservations.add(observation);
      }
    } else {
      // Greedy nearest-neighbour is optimal enough for <= 2 items on either side.
      const candidates: { track: Track; observation: Observation; cost: number }[] = [];
      for (const track of tracks) {
        for (const observation of observations) {
          const cost = this.cost(track, observation, timeMs, this.predictionA);
          if (Number.isFinite(cost)) candidates.push({ track, observation, cost });
        }
      }
      candidates.sort((a, b) => a.cost - b.cost);
      for (const candidate of candidates) {
        if (matchedTracks.has(candidate.track) || matchedObservations.has(candidate.observation)) {
          continue;
        }
        candidate.track.observe(candidate.observation, timeMs);
        matchedTracks.add(candidate.track);
        matchedObservations.add(candidate.observation);
      }
    }

    for (const track of tracks) {
      if (!matchedTracks.has(track)) track.markMissing(timeMs);
    }
    for (const observation of observations) {
      if (matchedObservations.has(observation)) continue;
      if (this.tracks.length >= 2) continue;
      this.spawn(observation, timeMs);
    }
  }

  private spawn(observation: Observation, timeMs: number): void {
    const track = new Track(`hand-${this.nextId}`);
    this.nextId += 1;
    track.setSmoothing(this.options.smoothing);
    track.observe(observation, timeMs);
    this.tracks.push(track);
  }

  private retire(timeMs: number): void {
    for (let i = this.tracks.length - 1; i >= 0; i -= 1) {
      const track = this.tracks[i];
      if (!track) continue;
      if (!track.visible && timeMs - track.lastSeen > this.options.trackTimeoutMs) {
        this.tracks.splice(i, 1);
      }
    }
  }
}
